import queryString from 'query-string';
import { Component } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType, FragmentType, UserRole, ValidationActionType } from 'models/enum';
import { ChartActionsComponentProps, ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { Locales, NAV_ID } from 'constants/app.constant';
import { appHelper, assignmentHelper, chartHelper } from 'helpers';
import { chartService } from 'services';
import { appSelectors } from 'redux/ducks/app';
import { studentSelectors } from 'redux/ducks/student';
import { withChartLogic } from 'components/common';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import { FormField, FormFieldLabel, SavedFormFieldLabel, SectionTitle } from './constants';
import DischargePlanningView from './DischargePlanningView';

const buildRequiredSanitizedFieldMap = (formFieldMap: Map<string, ChartMetaFormField>): Map<string, ChartMetaFormField> => {
  const sanitizedMap = new Map<string, ChartMetaFormField>();
  formFieldMap.forEach((field, name) => {
    sanitizedMap.set(name, {
      ...field,
      isRequired: false,
      errors: (field.errors || []).filter((error) => error?.type !== ValidationActionType.REQUIRED)
    });
  });
  return sanitizedMap;
};

interface DischargePlanningState {
  fragment: ChartFragment;
  multiselectResetKey: number;
}

interface DischargePlanningProps extends RouteComponentProps {
  showConfirmationModal: Function;
  userRole: string;
  authorName: string;
  locale?: Locales;
}

class DischargePlanning extends Component<DischargePlanningProps & ChartComponentProps, DischargePlanningState> {
  static displayName = 'DischargePlanning';

  constructor(props) {
    super(props);
    this.state = {
      fragment: null,
      multiselectResetKey: 0
    };
  }

  componentDidMount() {
    const id = this.getIdFromParam(this.props);
    this.loadData(id);
  }

  getIdFromParam = (props: DischargePlanningProps & ChartComponentProps) => {
    const parsedQuery = queryString.parse(props.location.search);
    return parsedQuery.id as string;
  };

  loadData = (id?: string) =>
    appHelper.useLoader(
      this.props.loadChartData([FragmentType.AUTHORED, FragmentType.CHARTING]).then(({ data: fragments }) => {
        let chartFragment: ChartFragment = null;
        if (id) {
          chartFragment = fragments.find((fragment) => fragment.fragmentId === id && fragment.active) ?? null;
        }
        this.setState({ fragment: chartFragment }, () => {
          this.props.initState(this.buildDefaultFormFields());
        });
      }),
      { errorMessage: 'Cannot load chart data' }
    );

  buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
    const { fragment } = this.state;
    const { createFormField, getFragmentValue, getFragmentContentIds } = chartHelper;
    const dataMap = new Map<string, ChartMetaFormField>();

    const valueFields = [
      { name: FormField.CAREGIVER_NAME, type: FormFieldInputType.TEXT_BOX, label: FormFieldLabel.CAREGIVER_NAME, sectionTitle: SectionTitle.CAREGIVER_INFORMATION },
      {
        name: FormField.DISCHARGE_INSTRUCTIONS_NAME,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.DISCHARGE_INSTRUCTIONS_NAME,
        sectionTitle: SectionTitle.CAREGIVER_INFORMATION
      },
      { name: FormField.OTHER_NEEDS, type: FormFieldInputType.TEXT_AREA, label: FormFieldLabel.OTHER_NEEDS, sectionTitle: SectionTitle.PRIOR_HEALTH_STATUS },
      {
        name: FormField.SERVICES_ESTABLISHED_OTHER,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.SERVICES_ESTABLISHED_OTHER,
        sectionTitle: SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS
      },
      {
        name: FormField.LIVING_ARRANGEMENT_ON_DISCHARGE_OTHER,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.LIVING_ARRANGEMENT_ON_DISCHARGE_OTHER,
        sectionTitle: SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS
      },
      {
        name: FormField.PHYSICAL_CHALLENGES_AT_HOME_OTHER,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.PHYSICAL_CHALLENGES_AT_HOME_OTHER,
        sectionTitle: SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS
      },
      {
        name: FormField.TRANSPORTATION_HOME_FROM_HOSPITAL_OTHER,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.TRANSPORTATION_HOME_FROM_HOSPITAL_OTHER,
        sectionTitle: SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS
      },
      {
        name: FormField.OTHER_MOTIVATIONAL_OBSERVATIONS,
        type: FormFieldInputType.TEXT_AREA,
        label: FormFieldLabel.OTHER_MOTIVATIONAL_OBSERVATIONS,
        sectionTitle: SectionTitle.CAREGIVER_INFORMATION
      },
      {
        name: FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL_OTHER,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL_OTHER,
        sectionTitle: SectionTitle.CAREGIVER_INFORMATION
      },
      {
        name: FormField.CAREGIVER_AT_HOME_OTHER,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.CAREGIVER_AT_HOME_OTHER,
        sectionTitle: SectionTitle.CAREGIVER_INFORMATION
      },
      {
        name: FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO_OTHER,
        type: FormFieldInputType.TEXT_BOX,
        label: FormFieldLabel.GIVE_DISCHARGE_INSTRUCTIONS_TO_OTHER,
        sectionTitle: SectionTitle.CAREGIVER_INFORMATION
      }
    ];

    const contentIdFields = [
      {
        name: FormField.CARE_STATUS_BEFORE_ADMISSION,
        type: FormFieldInputType.DROPDOWN,
        label: FormFieldLabel.CARE_STATUS_BEFORE_ADMISSION,
        sectionTitle: SectionTitle.PRIOR_HEALTH_STATUS
      },
      {
        name: FormField.LIVING_ARRANGEMENT_PRIOR_TO_ADMISSION,
        type: FormFieldInputType.DROPDOWN,
        label: FormFieldLabel.LIVING_ARRANGEMENT_PRIOR_TO_ADMISSION,
        sectionTitle: SectionTitle.PRIOR_HEALTH_STATUS
      },
      {
        name: FormField.LIVING_ARRANGEMENT_ON_DISCHARGE,
        type: FormFieldInputType.DROPDOWN,
        label: FormFieldLabel.LIVING_ARRANGEMENT_ON_DISCHARGE,
        sectionTitle: SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS
      },
      {
        name: FormField.TRANSPORTATION_HOME_FROM_HOSPITAL,
        type: FormFieldInputType.DROPDOWN,
        label: FormFieldLabel.TRANSPORTATION_HOME_FROM_HOSPITAL,
        sectionTitle: SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS
      },
      { name: FormField.CAREGIVER_AT_HOME, type: FormFieldInputType.DROPDOWN, label: FormFieldLabel.CAREGIVER_AT_HOME, sectionTitle: SectionTitle.CAREGIVER_INFORMATION },
      {
        name: FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO,
        type: FormFieldInputType.DROPDOWN,
        label: FormFieldLabel.GIVE_DISCHARGE_INSTRUCTIONS_TO,
        sectionTitle: SectionTitle.CAREGIVER_INFORMATION
      },
      {
        name: FormField.NEEDS_BEFORE_DISCHARGE,
        type: FormFieldInputType.MULTISELECT_DROPDOWN,
        label: FormFieldLabel.NEEDS_BEFORE_DISCHARGE,
        sectionTitle: SectionTitle.PRIOR_HEALTH_STATUS
      },
      {
        name: FormField.SERVICES_ESTABLISHED_AFTER_DISCHARGE,
        type: FormFieldInputType.MULTISELECT_DROPDOWN,
        label: FormFieldLabel.SERVICES_ESTABLISHED_AFTER_DISCHARGE,
        sectionTitle: SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS
      },
      {
        name: FormField.PHYSICAL_CHALLENGES_AT_HOME,
        type: FormFieldInputType.MULTISELECT_DROPDOWN,
        label: FormFieldLabel.PHYSICAL_CHALLENGES_AT_HOME,
        sectionTitle: SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS
      },
      {
        name: FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL,
        type: FormFieldInputType.MULTISELECT_DROPDOWN,
        label: FormFieldLabel.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL,
        sectionTitle: SectionTitle.CAREGIVER_INFORMATION
      }
    ];

    valueFields.forEach(({ name, type, label, sectionTitle }) => dataMap.set(name, createFormField({ name, type, label, value: getFragmentValue(fragment, sectionTitle, name) })));

    contentIdFields.forEach(({ name, type, label, sectionTitle }) => {
      if (type === FormFieldInputType.MULTISELECT_DROPDOWN) {
        const raw = getFragmentContentIds(fragment, sectionTitle, name)[0] || '';
        const contentIds = raw.split(',').filter(Boolean);
        dataMap.set(name, createFormField({ name, type, label, contentIds }));
      } else {
        dataMap.set(name, createFormField({ name, type, label, contentIds: getFragmentContentIds(fragment, sectionTitle, name) }));
      }
    });

    return dataMap;
  };

  buildFragment = () => {
    const { buildPatientRecord, buildPlainRecord } = chartService;
    const { formFieldMap, isAuthor, authorName, student } = this.props;
    const providerNurse = isAuthor ? (authorName || '').trim() : `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim();

    const isOptionSelected = (fieldKey: FormField, optionLabel: string): boolean => {
      const field = formFieldMap.get(fieldKey);
      return !!field?.chartContent?.some((c) => c.selected && c.label === optionLabel);
    };

    const buildMultiselectRecords = (fieldKey: FormField) => {
      const formField = formFieldMap.get(fieldKey);
      if (!formField?.value) return [];
      const selectedItems = formField.chartContent.filter((c) => c.selected);
      const noneOption = selectedItems.find((c) => c.label === 'None');
      const savedItems = noneOption ? [noneOption] : selectedItems;
      if (!selectedItems.length) return [];
      return [
        buildPlainRecord({
          formField: formField.name,
          title: formField.label,
          content: savedItems.map((c) => c.label).join(', '),
          value: formField.value,
          contentId: savedItems.map((c) => c.id).join(',')
        })
      ];
    };

    const record = {
      chartTitle: SectionTitle.DISCHARGE_PLANNING,
      fragmentTitle: SectionTitle.DISCHARGE_PLANNING,
      records: [
        {
          sectionTitle: SectionTitle.DISCHARGE_PLANNING,
          records: [
            buildPlainRecord({
              formField: FormField.PROVIDER_NURSE,
              title: FormFieldLabel.PROVIDER_NURSE,
              content: providerNurse,
              value: providerNurse
            })
          ]
        },
        {
          sectionTitle: SectionTitle.PRIOR_HEALTH_STATUS,
          records: [
            buildPatientRecord(formFieldMap, FormField.CARE_STATUS_BEFORE_ADMISSION),
            buildPatientRecord(formFieldMap, FormField.LIVING_ARRANGEMENT_PRIOR_TO_ADMISSION),
            ...buildMultiselectRecords(FormField.NEEDS_BEFORE_DISCHARGE),
            buildPatientRecord(formFieldMap, FormField.OTHER_NEEDS)
          ]
        },
        {
          sectionTitle: SectionTitle.DISCHARGE_SUMMARY_AND_INSTRUCTIONS,
          records: [
            ...buildMultiselectRecords(FormField.SERVICES_ESTABLISHED_AFTER_DISCHARGE),
            ...(isOptionSelected(FormField.SERVICES_ESTABLISHED_AFTER_DISCHARGE, 'Other')
              ? [buildPatientRecord(formFieldMap, FormField.SERVICES_ESTABLISHED_OTHER, SavedFormFieldLabel.SERVICES_ESTABLISHED_OTHER)]
              : []),
            buildPatientRecord(formFieldMap, FormField.LIVING_ARRANGEMENT_ON_DISCHARGE),
            ...(isOptionSelected(FormField.LIVING_ARRANGEMENT_ON_DISCHARGE, 'Other')
              ? [buildPatientRecord(formFieldMap, FormField.LIVING_ARRANGEMENT_ON_DISCHARGE_OTHER, SavedFormFieldLabel.LIVING_ARRANGEMENT_ON_DISCHARGE_OTHER)]
              : []),
            ...buildMultiselectRecords(FormField.PHYSICAL_CHALLENGES_AT_HOME),
            ...(isOptionSelected(FormField.PHYSICAL_CHALLENGES_AT_HOME, 'Other')
              ? [buildPatientRecord(formFieldMap, FormField.PHYSICAL_CHALLENGES_AT_HOME_OTHER, SavedFormFieldLabel.PHYSICAL_CHALLENGES_AT_HOME_OTHER)]
              : []),
            buildPatientRecord(formFieldMap, FormField.TRANSPORTATION_HOME_FROM_HOSPITAL),
            ...(isOptionSelected(FormField.TRANSPORTATION_HOME_FROM_HOSPITAL, 'Other')
              ? [buildPatientRecord(formFieldMap, FormField.TRANSPORTATION_HOME_FROM_HOSPITAL_OTHER, SavedFormFieldLabel.TRANSPORTATION_HOME_FROM_HOSPITAL_OTHER)]
              : [])
          ]
        },
        {
          sectionTitle: SectionTitle.CAREGIVER_INFORMATION,
          records: [
            buildPatientRecord(formFieldMap, FormField.CAREGIVER_AT_HOME),
            ...(isOptionSelected(FormField.CAREGIVER_AT_HOME, 'Other')
              ? [buildPatientRecord(formFieldMap, FormField.CAREGIVER_AT_HOME_OTHER, SavedFormFieldLabel.CAREGIVER_AT_HOME_OTHER)]
              : []),
            buildPatientRecord(formFieldMap, FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO),
            ...(isOptionSelected(FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO, 'Other')
              ? [buildPatientRecord(formFieldMap, FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO_OTHER, SavedFormFieldLabel.GIVE_DISCHARGE_INSTRUCTIONS_TO_OTHER)]
              : []),
            ...(isOptionSelected(FormField.CAREGIVER_AT_HOME, 'Self')
              ? []
              : [buildPatientRecord(formFieldMap, FormField.CAREGIVER_NAME, SavedFormFieldLabel.CAREGIVER_AT_HOME_NAME)]),
            ...(isOptionSelected(FormField.GIVE_DISCHARGE_INSTRUCTIONS_TO, 'Self')
              ? []
              : [buildPatientRecord(formFieldMap, FormField.DISCHARGE_INSTRUCTIONS_NAME, SavedFormFieldLabel.GIVE_DISCHARGE_INSTRUCTIONS_TO_NAME)]),
            ...buildMultiselectRecords(FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL),
            ...(isOptionSelected(FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL, 'Other')
              ? [buildPatientRecord(formFieldMap, FormField.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL_OTHER, SavedFormFieldLabel.FAMILY_CAREGIVER_MOTIVATIONAL_LEVEL_OTHER)]
              : []),
            buildPatientRecord(formFieldMap, FormField.OTHER_MOTIVATIONAL_OBSERVATIONS)
          ]
        }
      ]
    };

    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const fragmentId = this.state.fragment?.fragmentId || chartHelper.createFragmentId(NAV_ID.DISCHARGE_PLANNING_FORM);
    const basicInfo = chartService.createBaseFragment({ fragmentId, chartingTime: this.props.chartingTime });
    return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
  };

  handleSaveClick = () => {
    const fragment = this.buildFragment();
    const defaultFormFields = this.buildDefaultFormFields();
    const { isAuthor, formFieldMap } = this.props;
    const saveFormFieldMap = isAuthor ? buildRequiredSanitizedFieldMap(formFieldMap) : undefined;
    this.props.saveChart([fragment], {
      defaultFormFields,
      afterSave: this.props.navigateToSavedPatientCharting,
      ...(saveFormFieldMap ? { formFieldMap: saveFormFieldMap } : {})
    });
  };

  handleCancelClick = () => {
    this.setState((prevState) => ({ multiselectResetKey: prevState.multiselectResetKey + 1 }));
    appHelper.scrollTop();
  };

  render() {
    const { formFieldMap, formSubmittedCount, enableDisplayRecordsButton, hasUnsavedChanges, displayAuthoringData, handleDiscardClick, userRole, isAuthor, locale } = this.props;
    const defaultFormFields = this.buildDefaultFormFields();
    const isInstructorEditMode = assignmentHelper.isDemoEmptyEhr() || assignmentHelper.isInstructorAddDataToEhr(locale);
    const isInstructorReadOnly = userRole === UserRole.INSTRUCTOR && !isAuthor && !isInstructorEditMode;

    const chartActionsProps: ChartActionsComponentProps = {
      enableDisplayRecordsButton,
      enableSaveButton: hasUnsavedChanges,
      onCancelClick: () => handleDiscardClick(this.handleCancelClick, defaultFormFields),
      onDisplayRecordsClick: displayAuthoringData,
      onSaveClick: this.handleSaveClick,
      saveButtonText: 'Save & Complete',
      saveButtonHasIcon: true
    };

    const renderedFormFieldMap = isAuthor ? buildRequiredSanitizedFieldMap(formFieldMap) : formFieldMap;

    const viewProps = {
      chartActionsProps,
      formFieldMap: renderedFormFieldMap,
      formSubmittedCount,
      multiselectResetKey: this.state.multiselectResetKey,
      isReadOnly: isInstructorReadOnly,
      isAuthor
    };

    return <DischargePlanningView {...viewProps} />;
  }
}

export { DischargePlanning as BaseDischargePlanning };
const mapStateToProps = (state) => ({
  userRole: appSelectors.getUserRole(state),
  authorName: studentSelectors.getAuthorName(state),
  locale: appSelectors.getLocale(state)
});

export default compose(connect(mapStateToProps), withSavedPatientChartsPage, withChartLogic, withRouter)(DischargePlanning);
